import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  MapPin,
  Clock,
  Star,
  ArrowLeft,
  Building2,
  ChevronRight,
  Phone,
  ChevronLeft,
  Grid,
  List,
} from "lucide-react";
import type { CompanyBase, WorkingHours } from "../types/company";
import type { CategoryWithItems } from "../types/category";
import { apiService } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface RestaurantSelectionPageProps {}

enum ViewMode {
  Carousel = "carousel",
  Grid = "grid",
}

export function RestaurantSelectionPage({}: RestaurantSelectionPageProps) {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState<CompanyBase | null>(
    null
  );
  const [restaurants, setRestaurants] = useState<CompanyBase[]>([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState<CompanyBase[]>(
    []
  );
  const [restaurantCategories, setRestaurantCategories] = useState<
    Record<
      string,
      { preview: CategoryWithItems[]; all: CategoryWithItems[]; total: number }
    >
  >({});
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState(ViewMode.Grid);
  const [featuredRestaurant, setFeaturedRestaurant] =
    useState<CompanyBase | null>(null);
  const { t, language } = useLanguage();
  const isRTL = language === "ar" || language === "he";
  const carouselTopRef = useRef(null);

  const onRestaurantSelect = async (restaurant: CompanyBase) => {
    navigate(`/restaurants/${restaurant.id}`);
  };

  useEffect(() => {
    fetchRestaurants();
  }, [language]);

  useEffect(() => {
    filterRestaurants();
  }, [searchQuery, restaurants]);

  useEffect(() => {
    if (filteredRestaurants.length > 0) {
      setFeaturedRestaurant(
        filteredRestaurants[currentIndex] || filteredRestaurants[0]
      );
    }
  }, [filteredRestaurants, currentIndex]);

  const { i18n } = useTranslation();

  useEffect(() => {
    fetchRestaurants();
  }, [i18n.language]);

  const fetchRestaurants = async () => {
    setIsLoading(true);

    const response = await axios.get(
      `https://kaman.rest/api/companies?include_categories=true&lang=${i18n.language}`
    );
    const companies = response.data.data.companies;

    setRestaurants(companies);
    setFilteredRestaurants(companies);

    companies.forEach((company: any) => {
      setRestaurantCategories((prev) => ({
        ...prev,
        [company.id]: {
          preview: company.categories.slice(0, 3),
          all: company.categories,
          total: company.categories.length,
        },
      }));
    });
    // await fetchCategoriesForRestaurants(companies);
    setIsLoading(false);
  };

  const fetchCategoriesForRestaurants = async (restaurants: CompanyBase[]) => {
    try {
      const categoriesData: Record<
        string,
        {
          preview: CategoryWithItems[];
          all: CategoryWithItems[];
          total: number;
        }
      > = {};

      // Map each restaurant to a promise
      const promises = restaurants.map(async (restaurant) => {
        const categories = await apiService.getCategoriesWithItems(
          restaurant.id,
          language
        );
        const shuffled = [...categories].sort(() => 0.5 - Math.random());
        categoriesData[restaurant.id] = {
          preview: shuffled.slice(0, 3),
          all: categories,
          total: categories.length,
        };
      });

      // Await all promises at once
      await Promise.all(promises);

      setRestaurantCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const filterRestaurants = () => {
    if (!searchQuery.trim()) {
      setFilteredRestaurants(restaurants);
      setCurrentIndex(0);
      return;
    }

    const filtered = restaurants.filter(
      (restaurant) =>
        restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        restaurant.subdomain
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        (restaurant.settings?.address &&
          restaurant.settings.address
            .toLowerCase()
            .includes(searchQuery.toLowerCase()))
    );
    setFilteredRestaurants(filtered);
    setCurrentIndex(0);
  };

  const handleRestaurantSelect = (restaurant: CompanyBase) => {
    setFeaturedRestaurant(restaurant);
    if (onRestaurantSelect) onRestaurantSelect(restaurant);
  };

  const handleCategoryClick = (
    restaurant: CompanyBase,
    category: CategoryWithItems,
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    e.stopPropagation();

    navigate(`/restaurants/${restaurant.id}/categories/${category.id}`);
    // if (onCategorySelect) onCategorySelect(category);
  };

  const toggleCategoryExpansion = (
    restaurantId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    setExpandedCategories((prev) => ({
      ...prev,
      [restaurantId]: !prev[restaurantId],
    }));
  };

  function getTodayWorkingDay(workingHours: WorkingHours): any {
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ] as const;

    const todayIndex = new Date().getDay();
    const todayName = daysOfWeek[todayIndex];

    return workingHours[todayName];
  }

  const getCurrentDayStatus = (restaurant: CompanyBase) => {
    if (!restaurant.settings?.working_hours) return null;

    try {
      const workingHours = JSON.parse(restaurant.settings.working_hours);
      const todayHours = getTodayWorkingDay(workingHours);

      if (!todayHours || !todayHours.from || !todayHours.to) {
        return { isOpen: false, status: t("company.closed_today") };
      }

      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();

      const [fromHour, fromMin] = todayHours.from.split(":").map(Number);
      const [toHour, toMin] = todayHours.to.split(":").map(Number);

      const openTime = fromHour * 60 + fromMin;
      const closeTime = toHour * 60 + toMin;

      const isOpen = currentTime >= openTime && currentTime <= closeTime;

      return {
        isOpen,
        status: isOpen ? t("company.open_now") : t("company.closed_now"),
        hours: `${todayHours.from} - ${todayHours.to}`,
      };
    } catch (error) {
      return null;
    }
  };

  const nextRestaurant = () => {
    if (currentIndex < filteredRestaurants.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setFeaturedRestaurant(filteredRestaurants[newIndex]);
    }
  };

  const prevRestaurant = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setFeaturedRestaurant(filteredRestaurants[newIndex]);
    }
  };

  const goToRestaurant = (index: number) => {
    setCurrentIndex(index);
    setFeaturedRestaurant(filteredRestaurants[index]);
    if (viewMode === ViewMode.Carousel && carouselTopRef.current) {
      const y =
        (carouselTopRef.current as any).getBoundingClientRect().top +
        window.pageYOffset -
        70;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
          <p className="text-slate-600 text-lg font-medium"> {t("loading")} </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary/10">
      <div className="bg-primary backdrop-blur-xl border-b border-blue-200/50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col space-y-4 sm:space-y-6 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 mb-4 sm:mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-light text-white tracking-tight drop-shadow-lg truncate">
                  {t("restaurants.title")}
                </h1>
                <p className="text-sm sm:text-base text-white/90 font-light drop-shadow truncate">
                  <span className="hidden sm:inline">
                    {t("restaurants.subtitle")} •{" "}
                  </span>
                  {filteredRestaurants.length} {t("restaurants.found")}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
              <div className="flex bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm">
                <button
                  onClick={() => setViewMode(ViewMode.Carousel)}
                  className={`p-1.5 sm:p-2 rounded-md sm:rounded-lg transition-all ${
                    viewMode === ViewMode.Carousel
                      ? "bg-white shadow-md text-primary"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode(ViewMode.Grid)}
                  className={`p-1.5 sm:p-2 rounded-md sm:rounded-lg transition-all ${
                    viewMode === ViewMode.Grid
                      ? "bg-white shadow-md text-primary"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
              </div>

              {/* <div className="bg-white/90 backdrop-blur-sm rounded-lg p-1.5 sm:p-2 shadow-sm relative z-50">
                <LanguageSelector />
              </div> */}
            </div>
          </div>

          <div className="relative w-full max-w-none sm:max-w-2xl">
            <div className="absolute inset-y-0 start-0 ps-3 sm:ps-4 flex items-center pointer-events-none">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("restaurants.search_placeholder")}
              className="block w-full ps-10 sm:ps-12 pe-4 py-3 sm:py-4 bg-white/95 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all shadow-lg text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 sm:py-12">
        {filteredRestaurants.length === 0 ? (
          <div className="text-center py-12 sm:py-20">
            <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center shadow-lg">
              <Building2 className="w-8 h-8 sm:w-12 sm:h-12 text-primary" />
            </div>
            <h3 className="text-lg sm:text-xl font-light text-slate-900 mb-2">
              {searchQuery
                ? t("restaurants.no_results")
                : t("restaurants.no_restaurants")}
            </h3>
            <p className="text-slate-600 text-sm sm:text-base px-4">
              {searchQuery
                ? t("restaurants.try_different_search")
                : t("restaurants.check_back_later")}
            </p>
          </div>
        ) : viewMode === ViewMode.Carousel ? (
          <div ref={carouselTopRef} className="space-y-8 sm:space-y-12">
            {featuredRestaurant && (
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
                <div className="grid lg:grid-cols-[45%_55%]">
                  <div className="p-6 sm:p-8 lg:p-12 lg:pe-8">
                    <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                      {featuredRestaurant.settings?.logo ? (
                        <img
                          src={featuredRestaurant.settings.logo}
                          alt={featuredRestaurant.name}
                          className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl object-cover shadow-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                          <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-slate-900 mb-2 tracking-tight">
                          {featuredRestaurant.name}
                        </h2>
                        {(() => {
                          const dayStatus =
                            getCurrentDayStatus(featuredRestaurant);
                          return (
                            dayStatus && (
                              <div
                                className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium ${
                                  dayStatus.isOpen
                                    ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                    : "bg-red-100 text-red-800 border border-red-200"
                                }`}
                              >
                                <div
                                  className={`w-2 h-2 rounded-full me-2 ${
                                    dayStatus.isOpen
                                      ? "bg-emerald-500"
                                      : "bg-red-500"
                                  }`}
                                ></div>
                                {dayStatus.status}
                              </div>
                            )
                          );
                        })()}
                      </div>
                    </div>

                    {/* {featuredRestaurant.settings?.slogan && (
                      <blockquote className="text-lg sm:text-xl text-slate-700 font-light italic mb-6 sm:mb-8 border-s-4 border-slate-200 ps-4 sm:ps-6">
                        "{featuredRestaurant.settings.name}"
                      </blockquote>
                    )} */}

                    <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                      {featuredRestaurant.settings?.address && (
                        <div className="flex items-start text-slate-600">
                          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 me-2 sm:me-3 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm sm:text-base">
                            {featuredRestaurant.settings.address}
                          </span>
                        </div>
                      )}

                      {(() => {
                        const dayStatus =
                          getCurrentDayStatus(featuredRestaurant);
                        return (
                          dayStatus?.hours && (
                            <div className="flex items-center text-slate-600">
                              <Clock className="w-4 h-4 sm:w-5 sm:h-5 me-2 sm:me-3 text-slate-400 flex-shrink-0" />
                              <span className="text-sm sm:text-base">
                                Today: {dayStatus.hours}
                              </span>
                            </div>
                          )
                        );
                      })()}

                      {/* {featuredRestaurant.settings?.phone_number && (
                        <div className="flex items-center text-slate-600">
                          <Phone className="w-4 h-4 sm:w-5 sm:h-5 me-2 sm:me-3 text-slate-400 flex-shrink-0" />
                          <span className="text-sm sm:text-base">
                            {featuredRestaurant.settings.phone_number}
                          </span>
                        </div>
                      )} */}
                    </div>

                    <button
                      onClick={() => handleRestaurantSelect(featuredRestaurant)}
                      className="w-full sm:w-auto bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
                    >
                      {t("view_menu")}
                    </button>
                  </div>

                  <div className="p-6 sm:p-8 lg:p-12 lg:ps-8 bg-gradient-to-br from-blue-50 to-purple-50">
                    {restaurantCategories[featuredRestaurant.id] && (
                      <div>
                        <div className="flex items-center justify-between mb-4 sm:mb-6">
                          <h3 className="text-xl sm:text-2xl font-light text-slate-900">
                            {t("categories.categories")}
                          </h3>
                          {restaurantCategories[featuredRestaurant.id].total >
                            3 && (
                            <button
                              onClick={(e) =>
                                toggleCategoryExpansion(
                                  featuredRestaurant.id,
                                  e
                                )
                              }
                              className="text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                            >
                              {expandedCategories[featuredRestaurant.id]
                                ? t("categories.show_less")
                                : t("categories.show_all")}
                            </button>
                          )}
                        </div>
                        <div className="h-64 sm:h-80 overflow-hidden">
                          <div
                            className={
                              expandedCategories[featuredRestaurant.id]
                                ? "h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-primary scrollbar-track-transparent pe-2 sm:pe-5 ps-2 sm:ps-5 pb-3"
                                : "space-y-3 sm:space-y-4 gap-4 pe-2 sm:pe-4 ps-2 sm:ps-4"
                            }
                          >
                            <div className="space-y-3 sm:space-y-4">
                              {(expandedCategories[featuredRestaurant.id]
                                ? restaurantCategories[featuredRestaurant.id]
                                    .all
                                : restaurantCategories[featuredRestaurant.id]
                                    .preview
                              ).map((category) => (
                                <div
                                  key={category.id}
                                  onClick={(e) =>
                                    handleCategoryClick(
                                      featuredRestaurant,
                                      category,
                                      e
                                    )
                                  }
                                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl hover:shadow-md transition-all cursor-pointer transform hover:scale-105"
                                >
                                  <img
                                    src={category.image}
                                    alt={category.name}
                                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover flex-shrink-0"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-medium text-slate-900 truncate text-sm sm:text-base">
                                      {category.name}
                                    </h4>
                                    {/* <p className="text-xs sm:text-sm text-slate-600 truncate">
                                      {category.items_count ||
                                        category.items?.length ||
                                        0}{" "}
                                      {t("categories.items")}
                                    </p> */}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div
              dir={isRTL ? "rtl" : "ltr"}
              className="flex items-center justify-center gap-3 sm:gap-4"
            >
              <button
                onClick={prevRestaurant}
                disabled={currentIndex === 0}
                className={`p-2.5 sm:p-3 rounded-full transition-all ${
                  currentIndex === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-primary hover:bg-primary/10 shadow-lg hover:shadow-xl border border-primary/10"
                }`}
              >
                {isRTL ? (
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </button>

              <div className="flex items-center gap-1.5 sm:gap-2">
                {filteredRestaurants.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToRestaurant(index)}
                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all ${
                      index === currentIndex ? "bg-primary" : "bg-primary-light"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextRestaurant}
                disabled={currentIndex === filteredRestaurants.length - 1}
                className={`p-2.5 sm:p-3 rounded-full transition-all ${
                  currentIndex === filteredRestaurants.length - 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-primary hover:bg-primary-light shadow-lg hover:shadow-xl border border-blue-100"
                }`}
              >
                {isRTL ? (
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                ) : (
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                )}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {filteredRestaurants.map((restaurant, index) => (
                <button
                  key={restaurant.id}
                  onClick={() => goToRestaurant(index)}
                  className={`p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl text-start hover:shadow-lg transition-all transform hover:scale-105 border ${
                    index === currentIndex
                      ? "ring-2 ring-primary shadow-lg border-blue-200"
                      : "border-blue-100 hover:border-blue-200"
                  }`}
                >
                  {restaurant.settings?.logo ? (
                    <img
                      src={restaurant.settings.logo}
                      alt={restaurant.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover mb-2 sm:mb-3"
                    />
                  ) : (
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-200 to-purple-200 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                  )}
                  <h3 className="font-medium text-slate-900 truncate text-sm sm:text-base">
                    {restaurant.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 truncate">
                    {restaurant.settings?.address}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredRestaurants.map((restaurant) => {
              const dayStatus = getCurrentDayStatus(restaurant);
              const isSelected = selectedCompany?.id === restaurant.id;

              return (
                <div
                  key={restaurant.id}
                  onClick={() => handleRestaurantSelect(restaurant)}
                  className={`group cursor-pointer bg-white rounded-2xl sm:rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border ${
                    isSelected
                      ? "ring-2 ring-primary shadow-xl border-primary/10"
                      : "border-blue-100 hover:border-blue-200"
                  }`}
                >
                  <div className="h-2 bg-primary"></div>

                  <div className="p-6 sm:p-8">
                    <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                      {restaurant.settings?.logo ? (
                        <img
                          src={restaurant.settings.logo}
                          alt={restaurant.name}
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl object-cover shadow-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                          <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-light text-slate-900 truncate mb-2">
                          {restaurant.name}
                        </h3>
                        {dayStatus && (
                          <div
                            className={`inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                              dayStatus.isOpen
                                ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full me-2 ${
                                dayStatus.isOpen
                                  ? "bg-emerald-500"
                                  : "bg-red-500"
                              }`}
                            ></div>
                            {dayStatus.status}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2.5 sm:space-y-3 mb-4 sm:mb-6">
                      {restaurant.settings?.address && (
                        <div className="flex items-start text-xs sm:text-sm text-slate-600">
                          <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2 text-slate-400 flex-shrink-0 mt-0.5" />
                          <span className="truncate">
                            {restaurant.settings.address}
                          </span>
                        </div>
                      )}

                      {dayStatus?.hours && (
                        <div className="flex items-center text-xs sm:text-sm text-slate-600">
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2 text-slate-400 flex-shrink-0" />
                          <span>Today: {dayStatus.hours}</span>
                        </div>
                      )}

                      {/* {restaurant.settings?.phone_number && (
                        <div className="flex items-center text-xs sm:text-sm text-slate-600">
                          <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2 text-slate-400 flex-shrink-0" />
                          <span>{restaurant.settings.phone_number}</span>
                        </div>
                      )} */}
                    </div>

                    {/* {restaurant.settings?.slogan && (
                      <div className="mb-4 sm:mb-6 pt-4 sm:pt-6 border-t border-slate-100">
                        <p className="text-xs sm:text-sm text-slate-600 italic">
                          "{restaurant.settings.slogan}"
                        </p>
                      </div>
                    )} */}

                    {restaurantCategories[restaurant.id] && (
                      <div className="mb-4 sm:mb-6 pt-4 sm:pt-6 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3 sm:mb-4">
                          <h4 className="text-xs sm:text-sm font-medium text-slate-800">
                            {t("categories.categories")} (
                            {restaurantCategories[restaurant.id].total})
                          </h4>
                          {restaurantCategories[restaurant.id].total > 3 && (
                            <button
                              onClick={(e) =>
                                toggleCategoryExpansion(restaurant.id, e)
                              }
                              className="text-xs text-slate-600 hover:text-slate-800 transition-colors"
                            >
                              {expandedCategories[restaurant.id]
                                ? t("categories.show_less")
                                : t("categories.show_all")}
                            </button>
                          )}
                        </div>

                        <div
                          className={`${
                            expandedCategories[restaurant.id] &&
                            restaurantCategories[restaurant.id].all.length > 4
                              ? "h-28 sm:h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent pe-1 sm:pe-2"
                              : ""
                          }`}
                        >
                          <div className="grid grid-cols-2 gap-2">
                            {(expandedCategories[restaurant.id]
                              ? restaurantCategories[restaurant.id].all
                              : restaurantCategories[restaurant.id].preview
                            ).map((category) => (
                              <div
                                key={category.id}
                                onClick={(e) =>
                                  handleCategoryClick(restaurant, category, e)
                                }
                                className="flex items-center gap-2 p-2.5 sm:p-3 bg-slate-50 rounded-lg sm:rounded-xl hover:bg-slate-100 transition-all cursor-pointer flex-shrink-0"
                              >
                                <img
                                  src={category.image}
                                  alt={category.name}
                                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg object-cover flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium text-slate-900 truncate">
                                    {category.name}
                                  </p>
                                  {/* <p className="text-xs text-slate-600">
                                    {category.items_count ||
                                      category.items?.length ||
                                      0}{" "}
                                    {t("categories.items")}
                                  </p> */}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {isSelected && (
                      <div className="flex items-center justify-center bg-slate-900 text-white text-xs sm:text-sm font-medium py-2.5 sm:py-3 px-4 rounded-xl sm:rounded-2xl">
                        <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2 fill-current" />
                        {t("restaurants.currently_selected")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
